/**
 * Pregunta del punto y su resultado.
 *
 * Reglas que se reflejan en la interfaz:
 *  - intento unico, avisado antes de responder y no despues,
 *  - la explicacion base y la ampliada se muestran siempre, se acierte o no,
 *  - el acierto o el fallo se comunican con texto y con icono, no solo con
 *    color, y el resultado se anuncia a lectores de pantalla.
 *
 * Las opciones son un grupo de radios dentro de un fieldset con leyenda: es la
 * estructura que un lector de pantalla anuncia como "pregunta, opcion 2 de 4".
 *
 * Cada opcion es una tarjeta entera pulsable, con su letra en un sello. En la
 * calle se responde andando y con una mano: un radio de 24 pixeles es un
 * objetivo pequeño, la tarjeta completa no falla.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PreguntaPublica, ResultadoRespuesta } from '@recorregut/shared';
import { useEnviarRespuesta } from '@/api/consultas.ts';
import { etiquetaFormato } from '@/i18n';
import { MensajeError } from './MensajeError.tsx';
import { useAnuncios } from './Anuncio.tsx';

interface Props {
  pregunta: PreguntaPublica;
}

export function Pregunta({ pregunta }: Props) {
  const { t } = useTranslation();
  const { anunciar } = useAnuncios();
  const enviar = useEnviarRespuesta();

  const [seleccion, setSeleccion] = useState<string | null>(null);
  const [errorValidacion, setErrorValidacion] = useState<string | null>(null);
  const refGrupo = useRef<HTMLFieldSetElement | null>(null);
  const [resultado, setResultado] = useState<ResultadoRespuesta | null>(pregunta.resultado);

  useEffect(() => {
    setResultado(pregunta.resultado);
  }, [pregunta.resultado]);

  if (resultado) {
    return <ResultadoPregunta resultado={resultado} />;
  }

  return (
    <form
      noValidate
      onSubmit={(evento) => {
        evento.preventDefault();
        if (!seleccion) {
          setErrorValidacion(t('pregunta.capSeleccionada'));
          /*
            El foco vuelve a la primera opcion, como hace el formulario de
            inscripcion con su primer campo con error. Con `role="alert"` el
            aviso se oye una vez, pero quien responde caminando y con una mano
            necesita ademas quedarse DONDE hay que actuar, no en el boton que
            acaba de fallar.
          */
          refGrupo.current?.querySelector<HTMLElement>('input[type="radio"]')?.focus();
          return;
        }
        setErrorValidacion(null);
        enviar.mutate(
          { evento_punto_id: pregunta.evento_punto_id, respuesta_dada: seleccion },
          {
            onSuccess: (respuesta) => {
              setResultado(respuesta);
              anunciar(respuesta.correcta ? t('pregunta.correcta') : t('pregunta.incorrecta'));
            },
          },
        );
      }}
      className="space-y-5"
    >
      <fieldset ref={refGrupo} aria-describedby={errorValidacion ? 'error-pregunta' : undefined}>
        <legend className="text-base leading-relaxed font-medium text-tinta-900">
          {pregunta.enunciado}
        </legend>

        {/*
          El aviso del intento unico va ANTES de las opciones y no junto al
          boton: para que sirva de algo hay que leerlo mientras se decide, no
          cuando ya se ha decidido.
        */}
        <p className="aviso mt-3">{t('pregunta.intentUnic')}</p>

        <div className="mt-4 space-y-2">
          {pregunta.opciones.map((opcion) => {
            const elegida = seleccion === opcion.id;
            return (
              <label
                key={opcion.id}
                /*
                  `has-[:focus-visible]`: el radio esta oculto con `sr-only`,
                  asi que su anillo de foco no se veria. La tarjeta se lo pone
                  cuando el radio que lleva dentro tiene el foco de teclado. Sin
                  esto, navegar la pregunta con teclado seria a ciegas.
                */
                className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4
                            transition-colors has-[:focus-visible]:outline-2
                            has-[:focus-visible]:outline-offset-2
                            has-[:focus-visible]:outline-roig-600 ${
                              elegida
                                ? 'border-roig-600 bg-roig-50'
                                : 'border-crema-200 bg-white hover:border-tinta-400'
                            }`}
              >
                <input
                  type="radio"
                  name="respuesta"
                  value={opcion.id}
                  checked={elegida}
                  onChange={() => {
                    setSeleccion(opcion.id);
                    setErrorValidacion(null);
                  }}
                  className="sr-only"
                />
                {/*
                  El radio nativo queda oculto pero SIGUE siendo el control:
                  recibe el foco, se navega con las flechas y se anuncia como
                  radio. Lo que se dibuja es el sello con la letra, que es lo
                  que ya identifica a la opcion en la pregunta.
                */}
                <span
                  aria-hidden="true"
                  className={`sello uppercase ${
                    elegida
                      ? 'border-roig-600 bg-roig-600 text-white'
                      : 'border-crema-200 bg-crema-100 text-tinta-700'
                  }`}
                >
                  {opcion.id}
                </span>
                <span className="text-base leading-relaxed text-tinta-900">{opcion.texto}</span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {errorValidacion ? (
        <p id="error-pregunta" role="alert" className="error-campo">
          {errorValidacion}
        </p>
      ) : null}

      {enviar.isError ? <MensajeError error={enviar.error} /> : null}

      <button type="submit" className="boton-primario w-full" disabled={enviar.isPending}>
        {enviar.isPending ? t('app.carregant') : t('pregunta.respondre')}
      </button>
    </form>
  );
}

function ResultadoPregunta({ resultado }: { resultado: ResultadoRespuesta }) {
  const { t } = useTranslation();
  const formato = etiquetaFormato();

  return (
    <div className="space-y-5">
      <div className={resultado.correcta ? 'aviso-correcto' : 'aviso-error'}>
        <p className="flex items-center gap-2.5 text-base font-semibold text-tinta-900">
          {/* Icono ademas del color, y con el texto al lado: el color solo no
              transmite el resultado a quien no lo distingue. */}
          <span aria-hidden="true" className={resultado.correcta ? 'text-verd-600' : 'text-roig-600'}>
            {resultado.correcta ? (
              <svg viewBox="0 0 20 20" fill="none" className="size-5">
                <path
                  d="m4.5 10.5 3.5 3.5 7.5-8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" fill="none" className="size-5">
                <path
                  d="m5 5 10 10M15 5 5 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </span>
          {resultado.correcta ? t('pregunta.correcta') : t('pregunta.incorrecta')}
        </p>
        <p className="mt-1.5 font-titular text-lg font-semibold text-tinta-900">
          {t('pregunta.puntsObtinguts', {
            count: resultado.puntos_obtenidos,
            punts: new Intl.NumberFormat(formato).format(resultado.puntos_obtenidos),
          })}
        </p>
        {resultado.repetida ? (
          <p className="mt-2 text-sm text-tinta-700">{t('pregunta.haviaRespost')}</p>
        ) : null}
      </div>

      {resultado.explicacion_base || resultado.explicacion_ampliada ? (
        <section aria-labelledby="titulo-explicacio" className="space-y-2">
          <h3 id="titulo-explicacio" className="antetitulo">
            {t('pregunta.explicacio')}
          </h3>
          {resultado.explicacion_base ? (
            <p className="text-base leading-relaxed text-tinta-900">
              {resultado.explicacion_base}
            </p>
          ) : null}
          {resultado.explicacion_ampliada ? (
            <p className="text-base leading-relaxed text-tinta-700">
              {resultado.explicacion_ampliada}
            </p>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
